import { Box, Paper } from '@mui/material'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import Page from '@/components/layouts/Page'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { ConfigVersionDisplayEditable } from '@/features/controller/configuration/components/ConfigVersionDisplayEditable'
import { ConfigVersionDisplayReadOnly } from '@/features/controller/configuration/components/ConfigVersionDisplayReadOnly'
import { useControllerConfigurationFileContext } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import type { ControllerConfigType } from '@/features/controller/configuration/types'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { useControllerDescriptionQuery } from '@/features/controller/hooks/useControllerDescriptionQuery'
import { ControllerTabs } from '@/features/controller/pages/components/ControllerTabs'
import { ControllerRestartProvider } from '@/features/controller/restart/contexts/ControllerRestartContext'
import { renderControllerName } from '@/features/controller/utils/controllerNames'

interface ControllerConfigurationBasePageProps {
  activeTab: string
  configType: ControllerConfigType
  controllerId: string
  infoPanel?: ReactNode
  pageTitle: string
  schema?: object | null
}

export function ControllerConfigurationBasePage({
  activeTab,
  configType,
  controllerId,
  infoPanel,
  pageTitle,
  schema,
}: Readonly<ControllerConfigurationBasePageProps>) {
  const { t } = useTranslation()
  const { permissions } = usePermissions()

  const { controllerDescription } = useControllerDescriptionQuery(controllerId!, { enabled: Boolean(controllerId) })

  const { latestVersion, isLatestVersionLoading } = useControllerConfigurationFileContext()

  const canMutateControllers = permissions.has('controllersMutation')

  if (isLatestVersionLoading) {
    return <FullPageSpinner />
  }

  return (
    <Page
      pageHeaderProps={{
        appBarContent: (
          <CustomTypography>
            {t('controllers.controller')}{' '}
            {controllerDescription ? renderControllerName(controllerDescription) : controllerId!}
          </CustomTypography>
        ),
        breadcrumbItems: [
          {
            text: t('controllers.controller'),
            to: ControllerRouteInformation.CONTROLLER_DETAILS.navigationPath(controllerId),
          },
        ],
        pageTitle: pageTitle,
      }}
    >
      <ControllerTabs activeTab={activeTab} controllerId={controllerId} />
      {!latestVersion && (
        <Box my={2}>
          <Paper sx={{ p: 3 }}>{t('controllers.configuration.not_found', { configTypeName: configType.name })}</Paper>
        </Box>
      )}
      {latestVersion && canMutateControllers && (
        <ControllerRestartProvider>
          <ConfigVersionDisplayEditable
            configVersion={latestVersion}
            controllerId={controllerId}
            infoPanel={infoPanel}
            schema={schema}
          />
        </ControllerRestartProvider>
      )}
      {latestVersion && !canMutateControllers && (
        <ConfigVersionDisplayReadOnly configVersion={latestVersion} controllerId={controllerId} />
      )}
    </Page>
  )
}
